import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { siteSettingsApi } from '../services/api';
import { useGlobalLoader } from '../components/TimchehLoaderProvider';
import toast from 'react-hot-toast';

interface LoginForm {
  phoneNumber: string;
}

interface EmailPasswordForm {
  email: string;
  password: string;
}

interface OTPForm {
  otpCode: string;
}

const LoginPage: React.FC = () => {
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const { login, loginWithPassword, verifyOtp, isAuthenticated, isAdminAuthenticated, isInitialized } = useAuth();
  const { showLoader, hideLoader } = useGlobalLoader();
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch public site settings to determine login method
  const { data: siteSettings } = useQuery({
    queryKey: ['publicSiteSettings'],
    queryFn: async () => {
      const response = await siteSettingsApi.getPublic();
      return response.data;
    },
  });

  const from = location.state?.from?.pathname || '/profile';

  // Constants for OTP resend timer
  const OTP_RESEND_TIMEOUT = 120; // 2 minutes in seconds

  // Redirect authenticated users away from login page
  useEffect(() => {
    if (isInitialized) {
      if (isAdminAuthenticated) {
        navigate('/admin/dashboard', { replace: true });
      } else if (isAuthenticated) {
        navigate('/', { replace: true });
      }
    }
  }, [isInitialized, isAuthenticated, isAdminAuthenticated, navigate]);

  // Timer management
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            setIsResendDisabled(false);
            // Clear from localStorage when timer ends
            localStorage.removeItem(`otp_last_sent_${phoneNumber}`);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [countdown, phoneNumber]);

  // Check for existing timer on step change
  useEffect(() => {
    if (step === 'otp' && phoneNumber) {
      const lastSentKey = `otp_last_sent_${phoneNumber}`;
      const lastSentTime = localStorage.getItem(lastSentKey);
      
      if (lastSentTime) {
        const elapsed = Math.floor((Date.now() - parseInt(lastSentTime)) / 1000);
        const remaining = OTP_RESEND_TIMEOUT - elapsed;
        
        if (remaining > 0) {
          setCountdown(remaining);
          setIsResendDisabled(true);
        } else {
          localStorage.removeItem(lastSentKey);
        }
      }
    }
  }, [step, phoneNumber]);

  const startResendTimer = (phone: string) => {
    const lastSentKey = `otp_last_sent_${phone}`;
    const currentTime = Date.now().toString();
    localStorage.setItem(lastSentKey, currentTime);
    setCountdown(OTP_RESEND_TIMEOUT);
    setIsResendDisabled(true);
  };

  const {
    register: registerPhone,
    handleSubmit: handleSubmitPhone,
    formState: { errors: phoneErrors, isSubmitting: isSubmittingPhone }
  } = useForm<LoginForm>();

  const {
    register: registerEmailPassword,
    handleSubmit: handleSubmitEmailPassword,
    formState: { errors: emailPasswordErrors, isSubmitting: isSubmittingEmailPassword }
  } = useForm<EmailPasswordForm>();

  const {
    register: registerOTP,
    handleSubmit: handleSubmitOTP,
    formState: { errors: otpErrors, isSubmitting: isSubmittingOTP }
  } = useForm<OTPForm>();

  const onSubmitPhone = async (data: LoginForm) => {
    try {
      showLoader({ message: 'در حال ارسال کد تأیید...', fullScreen: true });
      
      console.log('Attempting to send OTP to:', data.phoneNumber);
      await login(data.phoneNumber);
      console.log('OTP sent successfully, switching to OTP step');
      setPhoneNumber(data.phoneNumber);
      setStep('otp');
      startResendTimer(data.phoneNumber);
      
      hideLoader();
      toast.success('کد تأیید به شماره تلفن شما ارسال شد!');
    } catch (error: any) {
      hideLoader();
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.message;
      
      if (errorMessage === 'کاربر یافت نشد') {
        toast.error('حساب کاربری یافت نشد. در حال هدایت به صفحه ثبت‌نام...', {
          duration: 3000
        });
        // Redirect to registration with the phone number pre-filled
        setTimeout(() => {
          navigate('/register', { 
            state: { phoneNumber: data.phoneNumber },
            replace: true 
          });
        }, 2000);
      } else {
        toast.error(errorMessage || 'ارسال کد تأیید با خطا مواجه شد');
      }
    }
  };

  const onSubmitEmailPassword = async (data: EmailPasswordForm) => {
    try {
      showLoader({ message: 'در حال ورود...', fullScreen: true });
      
      await loginWithPassword(data.email, data.password);
      
      hideLoader();
      toast.success('خوش آمدید!');
      navigate('/', { replace: true });
    } catch (error: any) {
      hideLoader();
      console.error('Email/password login error:', error);
      const errorMessage = error.response?.data?.message;
      toast.error(errorMessage || 'ایمیل یا رمز عبور نامعتبر است');
    }
  };

  const onSubmitOTP = async (data: OTPForm) => {
    try {
      showLoader({ message: 'در حال تأیید کد شما...', fullScreen: true });
      
      await verifyOtp(phoneNumber, data.otpCode);
      
      hideLoader();
      toast.success('خوش آمدید!');
      navigate(from, { replace: true });
    } catch (error: any) {
      hideLoader();
      const errorMessage = error.response?.data?.message;
      
      if (errorMessage === 'User not found') {
        toast.error('حساب کاربری یافت نشد. در حال هدایت به صفحه ثبت‌نام...');
        // Redirect to registration with the phone number pre-filled
        setTimeout(() => {
          navigate('/register', { 
            state: { phoneNumber: phoneNumber },
            replace: true 
          });
        }, 2000);
      } else {
        toast.error(errorMessage || 'کد تأیید نامعتبر است');
      }
    }
  };

  const resendOTP = async () => {
    if (isResendDisabled) {
      toast.error(`لطفاً ${countdown} ثانیه صبر کنید`);
      return;
    }

    try {
      await login(phoneNumber);
      startResendTimer(phoneNumber);
      toast.success('کد تأیید دوباره ارسال شد!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'ارسال مجدد کد تأیید با خطا مواجه شد');
    }
  };

  // Don't show login page if user is authenticated
  if (!isInitialized || isAuthenticated || isAdminAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            ورود به حساب کاربری
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            یا{' '}
            <Link
              to="/register"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              ایجاد حساب کاربری جدید
            </Link>
          </p>
        </div>

        {step === 'phone' ? (
          siteSettings?.enableOtpOnlyLogin ? (
            // OTP-only login form
            <form className="mt-8 space-y-6" onSubmit={handleSubmitPhone(onSubmitPhone)}>
              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
                  شماره تلفن
                </label>
                <input
                  {...registerPhone('phoneNumber', {
                    required: 'شماره تلفن الزامی است',
                    pattern: {
                      value: /^[\+]?[0-9]{10,15}$/,
                      message: 'لطفاً شماره تلفن معتبر وارد کنید'
                    }
                  })}
                  type="tel"
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="شماره تلفن خود را وارد کنید"
                />
                {phoneErrors.phoneNumber && (
                  <p className="mt-1 text-sm text-red-600">{phoneErrors.phoneNumber.message}</p>
                )}
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmittingPhone}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmittingPhone ? 'در حال ارسال کد...' : 'ارسال کد تأیید'}
                </button>
              </div>
            </form>
          ) : (
            // Email/Password login form
            <form className="mt-8 space-y-6" onSubmit={handleSubmitEmailPassword(onSubmitEmailPassword)}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  آدرس ایمیل
                </label>
                <input
                  {...registerEmailPassword('email', {
                    required: 'ایمیل الزامی است',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'لطفاً آدرس ایمیل معتبر وارد کنید'
                    }
                  })}
                  type="email"
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="آدرس ایمیل خود را وارد کنید"
                />
                {emailPasswordErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{emailPasswordErrors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  رمز عبور
                </label>
                <input
                  {...registerEmailPassword('password', {
                    required: 'رمز عبور الزامی است',
                    minLength: {
                      value: 6,
                      message: 'رمز عبور باید حداقل ۶ کاراکتر باشد'
                    }
                  })}
                  type="password"
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="رمز عبور خود را وارد کنید"
                />
                {emailPasswordErrors.password && (
                  <p className="mt-1 text-sm text-red-600">{emailPasswordErrors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <Link
                    to="/forgot-password"
                    className="font-medium text-primary-600 hover:text-primary-500"
                  >
                    رمز عبور را فراموش کرده‌اید؟
                  </Link>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmittingEmailPassword}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmittingEmailPassword ? 'در حال ورود...' : 'ورود'}
                </button>
              </div>
            </form>
          )
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmitOTP(onSubmitOTP)}>
            <div>
              <label htmlFor="otpCode" className="block text-sm font-medium text-gray-700">
                کد تأیید
              </label>
              <p className="text-sm text-gray-500 mb-2">
                کد ۶ رقمی ارسال شده به {phoneNumber} را وارد کنید
              </p>
              <input
                {...registerOTP('otpCode', {
                  required: 'کد تأیید الزامی است',
                  pattern: {
                    value: /^[0-9]{6}$/,
                    message: 'کد تأیید باید ۶ رقم باشد'
                  }
                })}
                type="text"
                maxLength={6}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm text-center text-2xl tracking-widest"
                placeholder="000000"
              />
              {otpErrors.otpCode && (
                <p className="mt-1 text-sm text-red-600">{otpErrors.otpCode.message}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setStep('phone')}
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                ← تغییر شماره تلفن
              </button>
              <button
                type="button"
                onClick={resendOTP}
                disabled={isResendDisabled}
                className={`text-sm ${
                  isResendDisabled 
                    ? 'text-gray-400 cursor-not-allowed' 
                    : 'text-primary-600 hover:text-primary-500'
                }`}
              >
                {isResendDisabled 
                  ? `ارسال مجدد (${Math.floor(countdown / 60)}:${(countdown % 60).toString().padStart(2, '0')})` 
                  : 'ارسال مجدد کد'
                }
              </button>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmittingOTP}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmittingOTP ? 'در حال تأیید...' : 'تأیید و ورود'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginPage; 
